import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutGrid, User, Target, CalendarCheck, FileText, PieChart, Bell, LifeBuoy, Info, GraduationCap, Award } from "lucide-react";
import CertificatesTab from "../components/portal/CertificatesTab";
import LearningTab from "../components/portal/LearningTab";
import PanelShell, { Card, StatCard } from "../components/panel/PanelShell";
import ProfileTab from "../components/portal/ProfileTab";
import GoalsTab from "../components/portal/GoalsTab";
import { ConsultationsTab, ReportsTab, MfResearchTab } from "../components/portal/RecordsTabs";
import { NotificationsTab, SupportTab } from "../components/portal/CommsTabs";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Seo from "../components/Seo";

function Overview({ stats, go, user }) {
  return (
    <div className="space-y-6">
      <div className="grain relative overflow-hidden rounded-3xl bg-navy p-8 text-white">
        <p className="text-xs uppercase tracking-[0.2em] text-gold">Client portal</p>
        <h2 className="mt-2 text-3xl font-bold" data-testid="portal-welcome">Welcome, {user.name?.split(" ")[0]}.</h2>
        <p className="mt-2 max-w-xl text-slate-400">Your financial goals, consultations, reports and support, all in one place.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => go("goals")} data-testid="overview-goals-btn" className="rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy">Manage goals</button>
          <Link to="/book-consultation" data-testid="overview-book-btn" className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold">Book consultation</Link>
          <Link to="/calculators" data-testid="overview-calculators-btn" className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold">Calculators</Link>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Goals" value={stats.goals ?? "-"} icon={Target} testid="stat-goals" />
        <StatCard label="Consultations" value={stats.consultations ?? "-"} icon={CalendarCheck} testid="stat-consultations" />
        <StatCard label="Reports" value={stats.reports ?? "-"} icon={FileText} testid="stat-reports" />
        <StatCard label="Unread alerts" value={stats.unread_notifications ?? "-"} icon={Bell} testid="stat-notifications" />
        <StatCard label="Open tickets" value={stats.open_tickets ?? "-"} icon={LifeBuoy} testid="stat-tickets" />
      </div>
      <Card className="flex gap-3 border-gold/40 bg-gold-50" data-testid="portal-holdings-note">
        <Info size={18} className="mt-0.5 shrink-0 text-gold-600" />
        <p className="text-sm text-slate-700">For your security, the portal does not display investment holdings or account balances. These will only be shown after verified data integrations and your explicit authorisation are in place.</p>
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const loadStats = useCallback(() => api.get("/portal/overview").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const nav = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "profile", label: "My Profile", icon: User },
    { key: "goals", label: "Financial Goals", icon: Target },
    { key: "consultations", label: "Consultation History", icon: CalendarCheck },
    { key: "reports", label: "Planning Reports", icon: FileText },
    { key: "mf", label: "Mutual Fund Research", icon: PieChart },
    { key: "learning", label: "My Learning", icon: GraduationCap },
    { key: "certificates", label: "Certificates", icon: Award },
    { key: "notifications", label: "Notifications", icon: Bell, badge: stats.unread_notifications },
    { key: "support", label: "Support", icon: LifeBuoy },
  ];
  const views = {
    overview: <Overview stats={stats} go={setTab} user={user} />,
    profile: <ProfileTab />,
    goals: <GoalsTab onChange={loadStats} />,
    consultations: <ConsultationsTab />,
    reports: <ReportsTab />,
    mf: <MfResearchTab />,
    learning: <LearningTab />,
    certificates: <CertificatesTab />,
    notifications: <NotificationsTab onChange={loadStats} />,
    support: <SupportTab onChange={loadStats} />,
  };
  return (
    <PanelShell label="Client Portal" nav={nav} active={tab} onChange={setTab} user={user} onLogout={async () => { await logout(); navigate("/login"); }}>
      <Seo title="Client Portal" noindex />
      <div data-testid={`portal-tab-${tab}`}>{views[tab]}</div>
    </PanelShell>
  );
}
